/**
 * Parsing rules of an `SdpFormatter.execPipeline` string, mirrored from the SDK
 * (`@kalyzee/kast-webrtc-client-module`). Kept free of any SDK import: this module
 * is the syntactic half of the SDP formatter validation, and the SDK's own entry
 * point drags in `@antmedia/webrtc_adaptor`, whose package.json declares an
 * absolute `main` that plain Node cannot resolve.
 *
 * Syntax: actions separated by `|`, each one `action[arg, ...]` where the
 * arguments are the JSON array starting at the first `[`.
 */

/** Splits a pipeline the way `execPipeline` does: newlines dropped, then `|`. */
export const splitSdpPipelineActions = (pipeline: string): string[] =>
  pipeline.replace(/(?:\r\n|\r|\n)/g, "").split("|");

/**
 * `execPipeline` lowercases the action name but never trims it, so the spaces
 * around a `|` become part of the name and the action is rejected as unknown:
 * `a["x"]|b[1]` works, `a["x"] | b[1]` does not. Returns the first action name
 * that carries surrounding whitespace — the padding, being invisible, makes the
 * SDK's "Action unknown" message unreadable on its own.
 */
export const findUntrimmedActionName = (pipeline: string): string | undefined => {
  const actions = splitSdpPipelineActions(pipeline);
  // A single action is fine: only a `|` can leave whitespace before a name, and
  // the SDK's own split gives the first action no leading padding.
  if (actions.length < 2) return undefined;
  for (const action of actions) {
    const [name] = action.split("[");
    if (name !== name.trim()) return name.trim();
  }
  return undefined;
};

/**
 * `execPipeline` reads the arguments as `JSON.parse("[".concat(strArgs))` where
 * `strArgs` is everything after the FIRST `[`, as an array of the remaining
 * `[`-separated pieces. Concatenating that array joins its parts with a comma,
 * so an argument that itself contains a `[` yields invalid JSON — the reason
 * `add_rtcp_fb` and `set_preferred_format`, which take an array, are out of
 * reach of the text form (only the callback form can pass one).
 *
 * Returns the name of the first action carrying such an argument, if any.
 */
export const findNestedArrayArgument = (pipeline: string): string | undefined => {
  for (const action of splitSdpPipelineActions(pipeline)) {
    const [name, ...args] = action.split("[");
    if (args.length > 1) return name.trim();
  }
  return undefined;
};
